/**
 * Pattern — Your Pattern page (Awakening Timeline synthesis).
 *
 * Awakening-tier only. Available after 7+ check-in entries.
 * On-demand generation via generate-pattern Edge Function.
 * Cached per period_key — never regenerates the same period twice.
 * Screenshot-optimized narrative display for sharing.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/supabase'
import {
  fetchTimeline, generatePattern, fetchEntryCount,
  type AwakeningTimeline, type PatternNarrative,
} from '../lib/thread'

interface Props { user: User }

// Labels come from t('pattern.periodDays', { count }) at render time — never hardcoded here.
const PERIODS: Array<{ days: 30 | 60 | 90 }> = [
  { days: 30 },
  { days: 60 },
  { days: 90 },
]

const MIN_ENTRIES = 7

function NarrativeCard({ narrative, entryCount, period, generatedAt }: {
  narrative: PatternNarrative
  entryCount: number
  period: 30 | 60 | 90
  generatedAt: string
}) {
  const { t, i18n } = useTranslation()
  const date = new Date(generatedAt).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })

  return (
    <div style={{
      background: 'white', borderRadius: '1.5rem',
      border: '1.5px solid #E4D5EE', overflow: 'hidden',
    }}>
      {/* Header band */}
      <div style={{ background: 'linear-gradient(135deg, #3D1F62, #7B40A8)', padding: '1.75rem 2rem' }}>
        <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', margin: '0 0 0.375rem' }}>
          {t('pattern.heading')}
        </p>
        <p style={{ fontFamily: 'var(--font-playfair, Georgia, serif)', fontSize: '1.25rem', color: 'white', margin: '0 0 0.375rem', fontWeight: 400 }}>
          {t('pattern.daysLabel', { count: period })}
        </p>
        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', margin: 0 }}>
          {t('pattern.entryCount', { count: entryCount, date })}
        </p>
      </div>

      {/* Narrative sections */}
      <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

        {/* Emotions */}
        <div>
          <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B72C0', margin: '0 0 0.625rem' }}>
            {t('pattern.sections.emotions')}
          </p>
          <p style={{ fontSize: '0.9375rem', color: '#1C1128', lineHeight: 1.75, margin: 0 }}>
            {narrative.emotions}
          </p>
        </div>

        {/* Pattern */}
        <div style={{ padding: '1.25rem 1.5rem', background: '#F4EFF8', borderRadius: '1rem', borderLeft: '4px solid #9B72C0' }}>
          <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B72C0', margin: '0 0 0.625rem' }}>
            {t('pattern.sections.pattern')}
          </p>
          <p style={{ fontFamily: 'var(--font-playfair, Georgia, serif)', fontSize: '1.0625rem', color: '#1C1128', lineHeight: 1.75, margin: 0, fontStyle: 'italic' }}>
            {narrative.pattern}
          </p>
        </div>

        {/* Gratitude — only if present */}
        {narrative.gratitude && (
          <div>
            <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#3A9B7A', margin: '0 0 0.625rem' }}>
              {t('patternPage.keptReturning')}
            </p>
            <p style={{ fontSize: '0.9375rem', color: '#1C1128', lineHeight: 1.75, margin: 0 }}>
              {narrative.gratitude}
            </p>
          </div>
        )}

        {/* Verbatim quote */}
        <div>
          <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#B87D0A', margin: '0 0 0.625rem' }}>
            {t('pattern.sections.quote')}
          </p>
          <blockquote style={{ margin: 0, borderLeft: '2px solid #E4D5EE', paddingLeft: '1rem' }}>
            <p style={{ fontFamily: 'var(--font-playfair, Georgia, serif)', fontSize: '0.9375rem', fontStyle: 'italic', color: '#6B5F7A', lineHeight: 1.8, margin: 0 }}>
              &ldquo;{narrative.quote}&rdquo;
            </p>
          </blockquote>
        </div>

        {/* Open question */}
        <div style={{ padding: '1.25rem 1.5rem', background: '#FAF7F4', borderRadius: '1rem', border: '1.5px solid #E4D5EE' }}>
          <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B8FAA', margin: '0 0 0.625rem' }}>
            {t('pattern.sections.question')}
          </p>
          <p style={{ fontSize: '0.9375rem', color: '#1C1128', lineHeight: 1.75, margin: 0 }}>
            {narrative.question}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function Pattern({ user }: Props) {
  const { t } = useTranslation()
  const navigate  = useNavigate()
  const [tier, setTier]           = useState<string>('free')
  const [entryCount, setEntryCount] = useState(0)
  const [period, setPeriod]       = useState<30 | 60 | 90>(30)
  const [timeline, setTimeline]   = useState<AwakeningTimeline | null>(null)
  const [generating, setGenerating] = useState(false)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState('')

  useEffect(() => {
    Promise.all([
      db().from('profiles').select('subscription_tier').eq('id', user.id).maybeSingle(),
      fetchEntryCount(user.id),
      fetchTimeline(user.id, 30),
    ]).then(([profileRes, count, cached]) => {
      const t = (profileRes.data as { subscription_tier: string } | null)?.subscription_tier ?? 'free'
      setTier(t)
      setEntryCount(count)
      if (cached) setTimeline(cached)
      setLoading(false)
    })
  }, [user.id])

  // Tier gate
  useEffect(() => {
    if (!loading && tier !== 'premium' && tier !== 'awakening') navigate('/')
  }, [loading, tier, navigate])

  async function switchPeriod(days: 30 | 60 | 90) {
    setPeriod(days)
    setTimeline(null)
    setError('')
    const cached = await fetchTimeline(user.id, days)
    if (cached) setTimeline(cached)
  }

  async function generate() {
    if (entryCount < MIN_ENTRIES) return
    setGenerating(true)
    setError('')
    const result = await generatePattern(period)
    setGenerating(false)
    if (result) setTimeline(result)
    else setError(t('errors.patternGenerate'))
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF7F4' }}>
        <div style={{ width: '1.25rem', height: '1.25rem', borderRadius: '50%', border: '2px solid #3D1F62', borderTopColor: 'transparent' }} className="animate-spin" />
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100svh', background: '#FAF7F4', fontFamily: 'var(--font-inter, system-ui, sans-serif)' }}>

      {/* Header */}
      <header style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', borderBottom: '1px solid #E4D5EE', background: 'white' }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9B8FAA', padding: '0.25rem', lineHeight: 0 }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M13 16l-6-6 6-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div>
          <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B72C0', margin: 0 }}>{t('pattern.heading')}</p>
          <p style={{ fontSize: '0.8125rem', color: '#9B8FAA', margin: 0 }}>{t('patternPage.reflectionAcross')}</p>
        </div>
      </header>

      <div style={{ maxWidth: '32rem', margin: '0 auto', padding: '2rem 1.5rem' }}>

        {entryCount < MIN_ENTRIES ? (
          /* Not enough entries yet */
          <div style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
            <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', background: '#F4EFF8', border: '1.5px solid #E4D5EE', margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="3" fill="#9B72C0" opacity="0.4" />
                <circle cx="10" cy="10" r="8" stroke="#9B72C0" strokeWidth="1.5" opacity="0.3" />
              </svg>
            </div>
            <p style={{ fontFamily: 'var(--font-playfair, Georgia, serif)', fontSize: '1.25rem', color: '#1C1128', margin: '0 0 0.75rem' }}>
              {t('patternPage.keepThreadGoing')}
            </p>
            <p style={{ fontSize: '0.9rem', color: '#6B5F7A', lineHeight: 1.75, margin: '0 0 1rem' }}>
              {t('pattern.notEnoughDesc', { min: MIN_ENTRIES })}
            </p>
            <p style={{ fontSize: '0.8125rem', color: '#9B8FAA', margin: '0 0 2rem' }}>
              {/* Plural key selected explicitly — i18next suffix resolution is not configured here. */}
              {`${t(entryCount === 1 ? 'pattern.checkInCount' : 'pattern.checkInCount_plural', { count: entryCount })} — ${t('pattern.toGo', { count: MIN_ENTRIES - entryCount })}`}
            </p>
            <button onClick={() => navigate('/thread')} style={{ padding: '0.75rem 1.75rem', borderRadius: '9999px', background: '#3D1F62', color: 'white', fontSize: '0.9rem', fontWeight: 500, border: 'none', cursor: 'pointer' }}>
              {t('pattern.checkInToday')}
            </button>
          </div>
        ) : (
          <>
            {/* Period selector */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.75rem' }}>
              {PERIODS.map(p => (
                <button
                  key={p.days}
                  onClick={() => switchPeriod(p.days)}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 500,
                    border: '1.5px solid',
                    borderColor: period === p.days ? '#3D1F62' : '#E4D5EE',
                    background: period === p.days ? '#3D1F62' : 'white',
                    color: period === p.days ? 'white' : '#6B5F7A',
                    cursor: 'pointer',
                  }}
                >
                  {t('pattern.periodDays', { count: p.days })}
                </button>
              ))}
            </div>

            {timeline ? (
              <NarrativeCard
                narrative={timeline.narrative}
                entryCount={timeline.entry_count}
                period={period}
                generatedAt={timeline.generated_at}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 1.5rem', background: 'white', borderRadius: '1.5rem', border: '1.5px solid #E4D5EE' }}>
                <p style={{ fontFamily: 'var(--font-playfair, Georgia, serif)', fontSize: '1.25rem', color: '#1C1128', margin: '0 0 0.75rem' }}>
                  {t('patternPage.readyWhenYouAre')}
                </p>
                <p style={{ fontSize: '0.9rem', color: '#6B5F7A', lineHeight: 1.75, margin: '0 0 1.75rem' }}>
                  {t('pattern.readyDesc', { period })}
                  {t('patternPage.readsBack')}
                </p>
                {error && <p style={{ fontSize: '0.875rem', color: '#C45272', margin: '0 0 1rem' }}>{error}</p>}
                <button
                  onClick={generate}
                  disabled={generating}
                  style={{
                    padding: '0.875rem 2rem', borderRadius: '9999px',
                    background: '#3D1F62', color: 'white', fontSize: '1rem',
                    fontWeight: 500, border: 'none', cursor: 'pointer',
                    opacity: generating ? 0.7 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: '0.625rem',
                  }}
                >
                  {generating && (
                    <span style={{ width: '1rem', height: '1rem', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', display: 'inline-block' }} className="animate-spin" />
                  )}
                  {generating ? t('pattern.generating') : t('pattern.generate', { period })}
                </button>
                {generating && (
                  <p style={{ fontSize: '0.8125rem', color: '#9B8FAA', marginTop: '1rem' }}>
                    {t('patternPage.readingThread')}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
